const messages: Record<string, string> = {
  INVALID_REQUEST: 'Dữ liệu gửi lên không hợp lệ. Vui lòng kiểm tra lại.',
  INVALID_SKU_SELECTION: 'Vui lòng chọn lại sản phẩm cần thêm vào giỏ.',
  SKU_NOT_IN_TEMPLATE: 'Danh sách sản phẩm đã thay đổi. Vui lòng tải lại đơn hàng mẫu.',
  TEMPLATE_NOT_FOUND: 'Không tìm thấy đơn hàng mẫu.',
  ORDER_NOT_FOUND: 'Không tìm thấy đơn hàng để tạo mẫu.',
  INVALID_NAME: 'Tên đơn hàng mẫu vượt giới hạn cho phép.',
  INVALID_SKU: 'Vui lòng chọn sản phẩm hợp lệ.',
  INVALID_QUANTITY: 'Số lượng sản phẩm không hợp lệ. Vui lòng kiểm tra lại.',
  ITEM_LIMIT_EXCEEDED: 'Số sản phẩm trong mẫu vượt giới hạn cho phép. Vui lòng giảm số sản phẩm.',
  TEMPLATE_LIMIT_EXCEEDED: 'Bạn đã đạt số lượng đơn hàng mẫu tối đa. Vui lòng xóa mẫu không dùng trước khi tạo mới.',
  TEMPLATE_EMPTY: 'Đơn hàng mẫu phải có ít nhất một sản phẩm.',
  CART_EMPTY: 'Giỏ hàng chưa có sản phẩm hợp lệ để tạo đơn hàng mẫu.',
  PRODUCT_NOT_FOUND: 'Không tìm thấy sản phẩm. Vui lòng chọn lại.',
  PRODUCT_OUT_OF_SCOPE: 'Sản phẩm không thuộc phạm vi kinh doanh của bạn.',
  PRODUCT_SERVICE_UNAVAILABLE: 'Chưa thể kiểm tra sản phẩm. Vui lòng thử lại sau.',
  USER_SERVICE_UNAVAILABLE: 'Chưa thể kiểm tra thông tin tài khoản. Vui lòng thử lại sau.'
};

export interface OrderTemplateApiError {
  message: string;
  status?: number;
  code?: string;
  traceId?: string;
  fieldErrors?: Record<string, string[]>;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

/** Accept both HttpErrorResponse and unsuccessful API wrappers. */
export function normalizeOrderTemplateError(error: unknown): OrderTemplateApiError {
  const response = asRecord(error);
  const body = 'error' in response ? asRecord(response['error']) : response;
  const status = typeof response['status'] === 'number' ? response['status']
    : typeof body['status'] === 'number' ? body['status'] : undefined;
  const backendMessage = typeof body['message'] === 'string' ? body['message'].trim() : '';
  const messageIsCode = /^[A-Z][A-Z0-9_]*$/.test(backendMessage);
  const code = typeof body['errorCode'] === 'string' && body['errorCode'].trim()
    ? body['errorCode'].trim() : messageIsCode ? backendMessage : undefined;
  const mappedMessage = code && Object.prototype.hasOwnProperty.call(messages, code) ? messages[code] : undefined;
  const errors = asRecord(body['errors']);
  const fieldErrors = Object.fromEntries(Object.entries(errors)
    .filter(([, values]) => Array.isArray(values))
    .map(([key, values]) => [key, (values as unknown[])
      .filter((value): value is string => typeof value === 'string' && !!value.trim())]));
  const validationMessage = [
    ...(fieldErrors[''] ?? []),
    ...Object.entries(fieldErrors).filter(([key]) => key !== '').flatMap(([, values]) => values)
  ].find(value => !!value.trim())?.trim();
  const isServiceUnavailable = status === 503 &&
    (code === 'PRODUCT_SERVICE_UNAVAILABLE' || code === 'USER_SERVICE_UNAVAILABLE');
  const canUseBackendMessage = status === undefined || (status > 0 && status < 500) || isServiceUnavailable;
  const preferredMessage = canUseBackendMessage
    ? validationMessage || (backendMessage && !messageIsCode ? backendMessage : undefined)
    : undefined;
  let message: string;

  if (status === 0) {
    message = 'Không thể kết nối. Vui lòng kiểm tra lại dữ liệu trước khi thử lại.';
  } else if (preferredMessage) {
    // Backend messages preserve configured limits, affected SKUs and account context.
    message = preferredMessage;
  } else if (status === 401) {
    message = 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.';
  } else if (status === 403) {
    message = 'Tài khoản chưa được phép thực hiện thao tác này.';
  } else if (status !== undefined && status >= 500) {
    message = isServiceUnavailable && mappedMessage
      ? mappedMessage : 'Hệ thống đang gặp sự cố. Vui lòng thử lại sau.';
  } else if (mappedMessage) {
    message = mappedMessage;
  } else if (status === 404) {
    message = 'Không tìm thấy dữ liệu yêu cầu. Vui lòng tải lại.';
  } else if (status === 400 || status === 422) {
    message = 'Dữ liệu gửi lên không hợp lệ. Vui lòng kiểm tra lại tên, sản phẩm và số lượng.';
  } else {
    message = 'Không thể thực hiện thao tác. Vui lòng tải lại dữ liệu và thử lại.';
  }

  return {
    message, status, code,
    traceId: typeof body['traceId'] === 'string' ? body['traceId'] : undefined,
    fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined
  };
}
